// Browser storage is a recovery cache. The authenticated database is authoritative.
// This module has no credentials or Supabase dependency, so its isolation contract
// can be tested without a database or browser.
export type AccountDocument = { document_id: string; payload: unknown; revision: number };
export interface AccountTransport {
  list(): Promise<AccountDocument[]>;
  write(id: string, payload: unknown, revision: number | null): Promise<AccountDocument>;
}
type Connection = {
  ownerId: string; transport: AccountTransport; records: Map<string, AccountDocument>;
  pending: Map<string, Promise<void>>; active: boolean;
};
let connection: Connection | null = null;
let lastFailure = '';
export function accountSaveFailure() { return lastFailure; }
function notify(message: string) {
  lastFailure = message;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('lavyn-account-save'));
}
export function disconnectAccountPersistence() {
  if (connection) connection.active = false;
  connection = null;
  lastFailure = '';
}
export async function connectAccountPersistence(ownerId: string, transport: AccountTransport) {
  disconnectAccountPersistence();
  const next: Connection = { ownerId, transport, records: new Map(), pending: new Map(), active: true };
  connection = next;
  try {
    const rows = await transport.list();
    if (!next.active || connection !== next) return false;
    for (const row of rows) {
      if (!row.document_id || !Number.isSafeInteger(row.revision) || row.revision < 1) throw new Error('Invalid saved record.');
      next.records.set(row.document_id, row);
    }
    return true;
  } catch {
    if (connection === next) {
      next.active = false;
      notify('Account saving is unavailable. Existing device copies are preserved; new changes are not confirmed saved to your account.');
    }
    return false;
  }
}
export function remoteAccountDocument(ownerId: string | null, id: string): { payload: unknown } | undefined {
  if (!ownerId || !connection?.active || connection.ownerId !== ownerId) return;
  return connection.records.get(id);
}
export function saveAccountDocument(ownerId: string | null, id: string, payload: unknown): Promise<void> {
  const target = connection;
  if (!ownerId || !target?.active || target.ownerId !== ownerId) return Promise.resolve();
  // Capture the content before a caller mutates its local arrays.
  const snapshot = JSON.parse(JSON.stringify(payload));
  const prior = target.pending.get(id) || Promise.resolve();
  let operation: Promise<void>;
  operation = prior.catch(() => {}).then(async () => {
    if (!target.active || connection !== target) throw new Error('Account changed before saving.');
    const expected = target.records.get(id)?.revision ?? null;
    const saved = await target.transport.write(id, snapshot, expected);
    if (!target.active || connection !== target) return;
    target.records.set(id, saved);
  }).finally(() => { if (target.pending.get(id) === operation) target.pending.delete(id); });
  // A failed write never becomes a blind overwrite on retry. The caller must
  // reload/reconcile a revision conflict; the local copy is left untouched.
  target.pending.set(id, operation);
  void operation.catch(() => notify('A change could not be saved to your account. Your device copy is preserved. Reload before retrying; another device may have changed this record.'));
  return operation;
}
